import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable, EMPTY, finalize, from, map, switchMap, takeWhile, tap, timeout, catchError, timer } from 'rxjs';
import {
  CreateCvPayload,
  CvApiService,
  CvListCards,
  CvListParams,
  InterviewInvitationPayload,
  PatchCvPayload,
  ShareLinkSendResult,
} from '../../../domain/api/cv-api.service';
import { AgeRange, Apprenti, ExtractionStatus } from '../../../domain/models';
import {
  FILTER_STORAGE_KEYS,
  FilterSelectionStorageService,
} from '../../../core/services/filter-selection-storage.service';
import { fromApiRecord } from './cvtheque.mapper';
import {
  isShareableCv,
} from './cv-statut.format';

export type CvthequeFilter =
  | 'tous'
  | 'non_places'
  | 'places'
  | 'cv_en_attente'
  | 'valid'
  | 'premiere_alternance'
  | 'archives';

export type ApprentiInput = Omit<Apprenti, 'id' | 'annotations'>;

const DEFAULT_LIMIT = 10;

const EMPTY_CARDS: CvListCards = {
  total: 0,
  place: 0,
  nonPlace: 0,
  enCours: 0,
  correctionEnAttente: 0,
};

export { isShareableCv };

function filterToListParams(
  filter: CvthequeFilter,
): Pick<CvListParams, 'placement' | 'premiereAlternance' | 'statutCv' | 'archived'> {
  switch (filter) {
    case 'non_places':
      return { placement: 'non_place' };
    case 'places':
      return { placement: 'place' };
    case 'premiere_alternance':
      return { premiereAlternance: true };
    case 'cv_en_attente':
      return { statutCv: 'in-progress' };
    case 'valid':
      return { statutCv: 'valid' };
    case 'archives':
      return { archived: true };
    default:
      return { archived: false };
  }
}

function buildListParams(
  filter: CvthequeFilter,
  search: string,
  conseillerIds: string[],
  formation: string | null,
  ageRanges: AgeRange[],
  inscription: 'inscrits_avec_groupe' | null,
  niveau: string | null,
): CvListParams {
  return {
    search,
    ...filterToListParams(filter),
    ...(conseillerIds.length ? { conseillerIds } : {}),
    ...(formation ? { formation } : {}),
    ...(ageRanges.length ? { ageRanges } : {}),
    ...(inscription ? { inscription } : {}),
    ...(niveau ? { niveau } : {}),
  };
}

@Injectable({ providedIn: 'root' })
export class CvthequeStore {
  private readonly api = inject(CvApiService);
  private readonly injector = inject(Injector);
  private readonly filterStorage = inject(FilterSelectionStorageService);

  private _apprentis = signal<Apprenti[]>([]);
  private _filter = signal<CvthequeFilter>('tous');
  private _search = signal('');
  private _conseillerIds = signal<string[]>(
    this.filterStorage.readIds(FILTER_STORAGE_KEYS.cvthequeConseillers),
  );
  private _formation = signal<string | null>(null);
  private _ageRanges = signal<AgeRange[]>([]);
  private _inscription = signal<'inscrits_avec_groupe' | null>(null);
  private _niveau = signal<string | null>(null);
  private _selectedId = signal<string | null>(null);
  private _selectedCvIds = signal<ReadonlySet<string>>(new Set());
  private _selectedShareableCvIds = signal<ReadonlySet<string>>(new Set());
  private _loading = signal(true);
  private _initialLoad = signal(true);
  private _page = signal(0);
  private _limit = signal(DEFAULT_LIMIT);
  private _totalRecords = signal(0);
  private _cards = signal<CvListCards>(EMPTY_CARDS);
  private _totalEnCours = signal(0);
  private _sharing = signal(false);
  private _sendingEmail = signal(false);
  private _patching = signal(false);
  private _uploadingCv = signal(false);

  private _archiving = signal(false);

  readonly filter = this._filter.asReadonly();
  readonly search = this._search.asReadonly();
  readonly conseillerIds = this._conseillerIds.asReadonly();
  readonly formation = this._formation.asReadonly();
  readonly ageRanges = this._ageRanges.asReadonly();
  readonly inscription = this._inscription.asReadonly();
  readonly niveau = this._niveau.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly sharing = this._sharing.asReadonly();
  readonly sendingEmail = this._sendingEmail.asReadonly();
  readonly patching = this._patching.asReadonly();
  readonly uploadingCv = this._uploadingCv.asReadonly();
  readonly archiving = this._archiving.asReadonly();
  readonly initialLoading = computed(
    () => this._loading() && this._initialLoad(),
  );
  readonly page = this._page.asReadonly();
  readonly limit = this._limit.asReadonly();
  readonly totalRecords = this._totalRecords.asReadonly();
  readonly selectedCvIds = this._selectedCvIds.asReadonly();

  readonly total = computed(() => this._cards().total);
  readonly placesCount = computed(() => this._cards().place);
  readonly nonPlacesCount = computed(() => this._cards().nonPlace);
  readonly aCorrigerCount = computed(() => this._cards().correctionEnAttente);
  /** Global total — used for the sidebar badge. */
  readonly cvEnAttenteCount = this._totalEnCours.asReadonly();
  readonly cards = this._cards.asReadonly();

  readonly apprentis = computed(() => this._apprentis());

  readonly shareableApprentis = computed(() =>
    this.apprentis().filter(isShareableCv),
  );

  readonly selectedCvCount = computed(() => this._selectedCvIds().size);

  readonly selectedShareableCvIds = computed(() => [
    ...this._selectedShareableCvIds(),
  ]);

  readonly selectedShareableCvCount = computed(
    () => this._selectedShareableCvIds().size,
  );

  readonly allSelected = computed(() => {
    const list = this._apprentis();
    if (list.length === 0) {
      return false;
    }
    const selected = this._selectedCvIds();
    return list.every((a) => selected.has(a.id));
  });

  readonly someSelected = computed(() => this._selectedCvIds().size > 0);

  readonly allShareableSelected = computed(() => {
    const shareable = this.shareableApprentis();
    if (shareable.length === 0) {
      return false;
    }
    const selected = this._selectedCvIds();
    return shareable.every((a) => selected.has(a.id));
  });

  readonly someShareableSelected = computed(() => {
    const selected = this._selectedCvIds();
    return this.shareableApprentis().some((a) => selected.has(a.id));
  });

  readonly selected = computed(
    () => this._apprentis().find((a) => a.id === this._selectedId()) ?? null,
  );

  constructor() {
    this.loadPage();
  }

  loadPage(page = this._page(), limit = this._limit()): void {
    this._loading.set(true);
    this.api
      .list({
        page,
        limit,
        ...buildListParams(
          this._filter(),
          this._search(),
          this._conseillerIds(),
          this._formation(),
          this._ageRanges(),
          this._inscription(),
          this._niveau(),
        ),
      })
      .subscribe({
      next: (response) => {
        this._page.set(response.page);
        this._limit.set(response.limit);
        this._totalRecords.set(response.total);
        this._cards.set(response.cards ?? EMPTY_CARDS);
        this._totalEnCours.set(response.totalEnCours);
        this._apprentis.set(response.cvs.map(fromApiRecord));
        this._loading.set(false);
        this._initialLoad.set(false);
      },
      error: () => {
        this._loading.set(false);
        this._initialLoad.set(false);
      },
    });
  }

  setFilter(filter: CvthequeFilter): void {
    this._filter.set(filter);
    this.loadPage(0, this._limit());
  }

  toggleInscription(): void {
    this._inscription.update((current) =>
      current === 'inscrits_avec_groupe' ? null : 'inscrits_avec_groupe',
    );
    this.loadPage(0, this._limit());
  }

  setSearch(search: string): void {
    this._search.set(search.trim());
    this.loadPage(0, this._limit());
  }

  setAdvancedFilters(filters: {
    conseillerIds?: string[];
    formation?: string | null;
    ageRanges?: AgeRange[];
    inscription?: 'inscrits_avec_groupe' | null;
    niveau?: string | null;
  }): void {
    if (filters.conseillerIds !== undefined) {
      this._conseillerIds.set(filters.conseillerIds);
      this.filterStorage.writeIds(
        FILTER_STORAGE_KEYS.cvthequeConseillers,
        filters.conseillerIds,
      );
    }
    if (filters.formation !== undefined) {
      this._formation.set(filters.formation);
    }
    if (filters.ageRanges !== undefined) {
      this._ageRanges.set(filters.ageRanges);
    }
    if (filters.inscription !== undefined) {
      this._inscription.set(filters.inscription);
    }
    if (filters.niveau !== undefined) {
      this._niveau.set(filters.niveau);
    }
    this.loadPage(0, this._limit());
  }

  isCvSelected(id: string): boolean {
    return this._selectedCvIds().has(id);
  }

  toggleCvSelection(id: string): void {
    const apprenti = this._apprentis().find((a) => a.id === id);
    const selecting = !this._selectedCvIds().has(id);

    this._selectedCvIds.update((current) => {
      const next = new Set(current);
      if (selecting) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

    this._selectedShareableCvIds.update((current) => {
      const next = new Set(current);
      if (selecting && apprenti && isShareableCv(apprenti)) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  toggleSelectAll(): void {
    const pageIds = this._apprentis().map((a) => a.id);
    if (!pageIds.length) {
      return;
    }

    if (this.allSelected()) {
      this.removeIdsFromSelection(pageIds);
      return;
    }

    this._selectedCvIds.update((current) => {
      const next = new Set(current);
      for (const id of pageIds) {
        next.add(id);
      }
      return next;
    });
    this._selectedShareableCvIds.update((current) => {
      const next = new Set(current);
      for (const a of this._apprentis()) {
        if (isShareableCv(a)) {
          next.add(a.id);
        }
      }
      return next;
    });
  }

  toggleSelectAllShareable(): void {
    const shareable = this.shareableApprentis();
    const shareableIds = shareable.map((a) => a.id);
    if (!shareableIds.length) {
      return;
    }

    if (this.allShareableSelected()) {
      this.removeIdsFromSelection(shareableIds);
      return;
    }

    this._selectedCvIds.update((current) => {
      const next = new Set(current);
      for (const id of shareableIds) {
        next.add(id);
      }
      return next;
    });
    this._selectedShareableCvIds.update((current) => {
      const next = new Set(current);
      for (const id of shareableIds) {
        next.add(id);
      }
      return next;
    });
  }

  clearCvSelection(): void {
    this._selectedCvIds.set(new Set());
    this._selectedShareableCvIds.set(new Set());
  }

  private removeIdsFromSelection(ids: string[]): void {
    this._selectedCvIds.update((current) => {
      const next = new Set(current);
      for (const id of ids) {
        next.delete(id);
      }
      return next;
    });
    this._selectedShareableCvIds.update((current) => {
      const next = new Set(current);
      for (const id of ids) {
        next.delete(id);
      }
      return next;
    });
  }

  createShareLink(): Observable<{ link: string }> {
    return this.api.createShareLink(this.selectedShareableCvIds());
  }

  createShareLinkFor(id: string): Observable<{ link: string }> {
    return this.api.createShareLink([id]);
  }

  archiveSelected(
    archived: boolean,
    options?: { reason?: string; commentaire?: string },
  ): Observable<{ updated: string[]; refused: { nuserId: string; reason: string }[] }> {
    const nuserIds = [...this._selectedCvIds()];
    this._archiving.set(true);
    return this.api
      .archiveBulk({
        nuserIds,
        archived,
        reason: options?.reason ?? 'CVthèque',
        commentaire: options?.commentaire ?? '',
      })
      .pipe(
        tap(() => {
          this.clearCvSelection();
          this.loadPage(this._page(), this._limit());
        }),
        finalize(() => this._archiving.set(false)),
      );
  }

  sendShareLinkToCompany(
    entrepriseId: string,
    representantId: string,
    offreId?: string,
  ): Observable<ShareLinkSendResult> {
    this._sharing.set(true);
    return this.api.sendShareLink({
      cvIds: this.selectedShareableCvIds(),
      entrepriseId,
      representantId,
      ...(offreId ? { offreId } : {}),
    });
  }

  sendInterviewInvitation(
    payload: InterviewInvitationPayload,
  ): Observable<{ entretienId: string; sentTo: string; responseLink: string }> {
    this._sendingEmail.set(true);
    return this.api.sendInterviewInvitation(payload).pipe(
      finalize(() => this._sendingEmail.set(false)),
      map((response) => ({
        entretienId: response.entretienId,
        sentTo: response.sentTo,
        responseLink: response.responseLink,
      })),
    );
  }

  sendCvUpdateRequest(
    apprentiIds: string[],
  ): Observable<{ sentCount: number; failed: { apprentiId: string; reason: string }[] }> {
    this._sendingEmail.set(true);
    return this.api.sendCvUpdateRequest(apprentiIds).pipe(
      finalize(() => this._sendingEmail.set(false)),
      map((response) => ({
        sentCount: response.sentCount,
        failed: response.failed ?? [],
      })),
    );
  }

  finishShare(): void {
    this._sharing.set(false);
    this.clearCvSelection();
  }

  failShare(): void {
    this._sharing.set(false);
  }

  openPanel(id: string): void {
    if (this._uploadingCv()) {
      return;
    }
    this._selectedId.set(id);
    this.refreshApprenantDetail(id).subscribe();
  }

  refreshApprenantDetail(id: string): Observable<Apprenti> {
    return this.api.getById(id).pipe(
      tap((record) => this.applyApprenantFromApi(id, record)),
      map((record) => fromApiRecord(record)),
    );
  }

  closePanel(): void {
    if (this._uploadingCv()) {
      return;
    }
    this._selectedId.set(null);
  }

  createCv(payload: CreateCvPayload): void {
    this.api.create(payload).subscribe({
      next: () => this.loadPage(0, this._limit()),
    });
  }

  assignCommercial(id: string, commercialId: string): Observable<Apprenti> {
    const apprenti = this._apprentis().find((a) => a.id === id);
    const statutCv = apprenti?.statutCv ?? 'not-created';
    return this.patchCv(id, { statutCv, conseillerAdministratifId: commercialId });
  }

  setArchived(
    id: string,
    archived: boolean,
    options?: { reason?: string; commentaire?: string },
  ): Observable<Apprenti> {
    const apprenti = this._apprentis().find((a) => a.id === id);
    const statutCv = apprenti?.statutCv ?? 'not-created';
    return this.patchCv(id, {
      statutCv,
      archived,
      reason: options?.reason,
      commentaire: options?.commentaire,
    }).pipe(
      tap(() => {
        const filter = this._filter();
        if ((archived && filter !== 'archives') || (!archived && filter === 'archives')) {
          this.loadPage(this._page(), this._limit());
        }
      }),
    );
  }

  remove(id: string): void {
    const removed = this._apprentis().find((a) => a.id === id);
    this._apprentis.update((list) => list.filter((a) => a.id !== id));
    this._totalRecords.update((total) => Math.max(total - 1, 0));
    if (removed) {
      this._cards.update((cards) => ({
        total: Math.max(cards.total - 1, 0),
        place:
          removed.placement === 'place'
            ? Math.max(cards.place - 1, 0)
            : cards.place,
        nonPlace:
          removed.placement === 'non_place'
            ? Math.max(cards.nonPlace - 1, 0)
            : cards.nonPlace,
        enCours:
          removed.statutCv === 'in-progress'
            ? Math.max(cards.enCours - 1, 0)
            : cards.enCours,
        correctionEnAttente:
          removed.statutCv === 'correction-pending'
            ? Math.max(cards.correctionEnAttente - 1, 0)
            : cards.correctionEnAttente,
      }));
    }
    this._selectedCvIds.update((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    this._selectedShareableCvIds.update((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    if (this._selectedId() === id) {
      this._selectedId.set(null);
    }
  }

  attachCvFile(id: string, file: File): Observable<Apprenti> {
    return this.uploadAndPatchCv(id, file, { statutCv: 'valid' }).pipe(
      tap((apprenti) => this.pollExtractionStatusIfNeeded(id, apprenti.extractionStatus)),
    );
  }

  reExtractCv(id: string): Observable<Apprenti> {
    return this.api.extractCv(id).pipe(
      tap((record) => this.applyApprenantFromApi(id, record)),
      map((record) => fromApiRecord(record)),
      tap((apprenti) => this.pollExtractionStatusIfNeeded(id, apprenti.extractionStatus)),
    );
  }

  addAnnotation(id: string, author: string, text: string): Observable<Apprenti> {
    const apprenti = this._apprentis().find((a) => a.id === id);
    if (apprenti?.statutCv !== 'correction-pending') {
      throw new Error(
        'Les annotations supplémentaires sont disponibles uniquement en correction.',
      );
    }
    return this.patchCv(id, {
      statutCv: 'correction-pending',
      annotations: [{ text, author }],
    });
  }

  validateCv(id: string, file?: File): Observable<Apprenti> {
    if (file) {
      return this.uploadAndPatchCv(id, file, { statutCv: 'valid' }).pipe(
        tap((apprenti) => this.pollExtractionStatusIfNeeded(id, apprenti.extractionStatus)),
      );
    }
    return this.patchCv(id, { statutCv: 'valid' });
  }

  requestCorrection(
    id: string,
    annotation: { text: string; author?: string },
  ): Observable<Apprenti> {
    return this.patchCv(id, {
      statutCv: 'correction-pending',
      annotations: [annotation],
    });
  }

  patchCv(id: string, payload: PatchCvPayload): Observable<Apprenti> {
    this._patching.set(true);
    return this.api.patchCv(id, payload).pipe(
      tap((record) => this.applyApprenantFromApi(id, record)),
      map((record) => fromApiRecord(record)),
      finalize(() => this._patching.set(false)),
    );
  }

  private uploadAndPatchCv(
    id: string,
    file: File,
    payload: Omit<PatchCvPayload, 'file' | 'cvFilePath' | 'cvFileName'>,
  ): Observable<Apprenti> {
    this._uploadingCv.set(true);
    this._patching.set(true);
    return from(import('../../../core/services/azure-storage.service')).pipe(
      switchMap(({ AzureStorageService }) => {
        const azure = this.injector.get(AzureStorageService);
        return from(azure.uploadCvFile(id, file));
      }),
      switchMap(({ cvFilePath, cvFileName }) =>
        this.api.patchCv(id, { ...payload, cvFilePath, cvFileName }),
      ),
      tap((record) => this.applyApprenantFromApi(id, record)),
      map((record) => fromApiRecord(record)),
      finalize(() => {
        this._uploadingCv.set(false);
        this._patching.set(false);
      }),
    );
  }

  private applyApprenantFromApi(id: string, record: unknown): void {
    const updated = fromApiRecord(record);
    const apprenti: Apprenti = { ...updated, id: updated.id || id };

    this._apprentis.update((list) =>
      list.map((a) => (a.id === id ? apprenti : a)),
    );

    if (!isShareableCv(apprenti)) {
      this._selectedShareableCvIds.update((current) => {
        if (!current.has(id)) {
          return current;
        }
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    } else if (this._selectedCvIds().has(id)) {
      this._selectedShareableCvIds.update((current) => {
        if (current.has(id)) {
          return current;
        }
        const next = new Set(current);
        next.add(id);
        return next;
      });
    }

    this.refreshKpis();
  }

  private pollExtractionStatusIfNeeded(
    id: string,
    status?: ExtractionStatus,
  ): void {
    if (status !== 'pending' && status !== 'processing') {
      return;
    }

    timer(0, 2000)
      .pipe(
        switchMap(() => this.api.getById(id)),
        tap((record) => this.applyApprenantFromApi(id, record)),
        map((record) => fromApiRecord(record).extractionStatus ?? 'idle'),
        takeWhile(
          (nextStatus) => nextStatus === 'pending' || nextStatus === 'processing',
          true,
        ),
        timeout(60_000),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  /** Re-fetch filter-aware KPI cards without reloading the table. */
  private refreshKpis(): void {
    this.api
      .list({
        page: this._page(),
        limit: this._limit(),
        ...buildListParams(
          this._filter(),
          this._search(),
          this._conseillerIds(),
          this._formation(),
          this._ageRanges(),
          this._inscription(),
          this._niveau(),
        ),
      })
      .subscribe({
        next: (response) => {
          this._cards.set(response.cards ?? EMPTY_CARDS);
          this._totalEnCours.set(response.totalEnCours);
        },
      });
  }

  /**
   * Business rule: while the CV is not validated, the conseiller annotations
   * are visible to the apprentice in their portal.
   */
  annotationsVisibleToStudent(apprenti: Apprenti): boolean {
    return apprenti.statutCv !== 'valid';
  }
}
